import { apiClient } from '@/lib/apiClient';
import type {
  Campaign,
  CampaignStatus,
  CommunicationMessage,
  CommunicationTemplate,
} from '../types';

export interface AudienceFilters {
  isActive?: boolean;
  hasPushToken?: boolean;
  timezones?: string[];
  languages?: string[];
  streakMin?: number;
  streakMax?: number;
  hasTeam?: boolean;
  teamStatuses?: string[];
  onboardingPassed?: boolean;
  hasCheckedIn?: boolean;
}

export type CampaignTargeting =
  | { type: 'all'; filters?: AudienceFilters }
  | { type: 'userIds'; userIds: string[]; filters?: AudienceFilters };

export interface CreateCampaignBody {
  name: string;
  content: {
    push?: { title: string; body: string; imageUrl?: string };
    inApp?: { title: string; body: string; iconType?: string; imageUrl?: string };
  };
  channels: ('push' | 'in_app')[];
  targeting: CampaignTargeting;
  targetRoute?: string;
  templateSlug?: string;
}

export interface AudiencePreviewResponse {
  matchCount: number;
  sample?: { userId: string; email?: string; displayName?: string }[];
}

export const communicationApi = {
  listTemplates: (includeInactive = false) =>
    apiClient.get<{ templates: CommunicationTemplate[] }>('/communication/templates', {
      includeInactive,
    }),

  getTemplate: (templateId: string) =>
    apiClient.get<{ template: CommunicationTemplate }>(
      `/communication/templates/${encodeURIComponent(templateId)}`,
    ),

  deleteTemplate: (templateId: string) =>
    apiClient.delete<{ success: boolean }>(
      `/communication/templates/${encodeURIComponent(templateId)}`,
    ),

  updateTemplate: (templateId: string, body: Partial<CommunicationTemplate>) =>
    apiClient.put<{ template: CommunicationTemplate }>(
      `/communication/templates/${encodeURIComponent(templateId)}`,
      body,
    ),

  listMessages: (params: { limit?: number; cursor?: string } = {}) =>
    apiClient.get<{ messages: CommunicationMessage[]; nextCursor?: string | null }>(
      '/communication/messages',
      { ...params },
    ),

  getMessage: (messageId: string) =>
    apiClient.get<{ message: CommunicationMessage }>(
      `/communication/messages/${encodeURIComponent(messageId)}`,
    ),

  listCampaigns: (params: { limit?: number; cursor?: string; status?: CampaignStatus } = {}) =>
    apiClient.get<{ campaigns: Campaign[]; nextCursor?: string | null }>(
      '/communication/campaigns',
      { ...params },
    ),

  getCampaign: (campaignId: string) =>
    apiClient.get<{ campaign: Campaign }>(
      `/communication/campaigns/${encodeURIComponent(campaignId)}`,
    ),

  sendCampaign: (campaignId: string) =>
    apiClient.post<{ campaign: Campaign }>(
      `/communication/campaigns/${encodeURIComponent(campaignId)}/send`,
    ),

  cancelCampaign: (campaignId: string) =>
    apiClient.post<{ campaign: Campaign }>(
      `/communication/campaigns/${encodeURIComponent(campaignId)}/cancel`,
    ),

  createCampaign: (body: CreateCampaignBody) =>
    apiClient.post<{ campaign: Campaign }>('/communication/campaigns', body),

  testSendCampaign: (campaignId: string, testUserIds: string[]) =>
    apiClient.post<{ sent: number }>(
      `/communication/campaigns/${encodeURIComponent(campaignId)}/test-send`,
      { testUserIds },
    ),

  previewAudience: (filters: AudienceFilters) =>
    apiClient.post<AudiencePreviewResponse>('/communication/audience/preview', { filters }),
};
